<template>
  <popup-dialog
    class="quickinfo-popup"
    :backdrop="false"
    :x="position.x"
    :y="position.y"
    :teleport-target="teleportTarget"
    :resizable="true"
    :resizable-edges="{
      top: false,
      left: false,
      bottom: false,
      right: true
    }"
    x-alignment="left"
    y-alignment="top"
    :min-width="minWidth ?? 200"
    :min-height="undefined"
    @close="closePopup()"
    @pointerenter="onPointerEnter"
    @pointerleave="onPointerLeave"
  >
    <template #header="{ close }">
      <div class="quickinfo-popup__header">
        <span class="quickinfo-popup__title">
          <span class="mt-1 label">
            <slot name="label"></slot>
          </span>
          <h3 class="quickinfo-popup__titletext mt-0">
            <slot name="title"></slot>
          </h3>
        </span>

        <button
          v-if="infoIconVisible"
          class="button-icon button-icon_small ml-auto"
          type="button"
          style="margin-top: -3px; padding: 0.5rem; padding-top: 0.625rem"
          @click="$emit('info-click', $event)"
        >
          <lucide-icon name="info" />
        </button>
        <template v-else>
          <slot name="header-center"></slot>
        </template>

        <button
          class="button-icon button-icon_small quickinfo-popup__closebutton"
          type="button"
          @click="close"
        >
          <lucide-icon name="x" />
        </button>
      </div>
    </template>
    <article class="quickinfo-popup__content">
      <div v-if="$slots['before-content']" class="quickinfo-popup__before">
        <slot name="before-content"></slot>
      </div>

      <slot></slot>
    </article>
  </popup-dialog>
</template>

<script setup lang="ts">
import PopupDialog from "@core/ui/PopupDialog.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

interface Props {
  position: { x: number; y: number };
  active?: boolean;
  infoIconVisible?: boolean;
  minWidth?: number;
  teleportTarget?: string | null;
}
withDefaults(defineProps<Props>(), {
  active: false,
  infoIconVisible: false,
  minWidth: undefined,
  teleportTarget: "#app"
});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "update:active", value: boolean): void;
  (e: "info-click", value: MouseEvent): void;
}>();

function closePopup() {
  emit("close");
  emit("update:active", false);
}
function onPointerEnter() {
  emit("update:active", true);
}
function onPointerLeave() {
  emit("update:active", false);
}
</script>

<style lang="scss">
.quickinfo-popup {
  background-color: var(--color-background-deep);

  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  border-radius: 5px;
  width: min-content;
  max-width: max-content;
  user-select: none; // for firefox resizing

  &:hover {
    outline: 1px solid var(--color-action-transparent);
  }

  &__header {
    background-color: var(--color-background-shallow);
    width: 100%;
    display: grid;
    grid-template-columns: minmax(5rem, 1fr) min-content min-content;
    align-items: start;
    padding-left: 1rem;
    padding-right: 0.5rem;
    padding-block: 0.25rem;
    min-width: 200px;
  }

  &__title {
    display: flex;
    flex-direction: column;
    align-items: start;
    text-align: left;
    padding-right: 1rem;
    min-width: 5rem;
    max-width: 100%;
    width: max-content;
  }

  &__titletext {
    font-size: 1rem;
    min-width: 0;
    width: 100%;
    max-height: 4.65em;
    overflow-wrap: break-word;
    text-overflow: ellipsis;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    -webkit-box-orient: vertical;
    hyphens: auto;
    margin-bottom: 0;
    padding-bottom: 0.25rem;
  }

  &__content {
    padding-top: 0.5rem;
    padding-bottom: 1rem;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  &__before {
    font-size: 13px;
    color: var(--color-text-light);
    margin-bottom: 0.5rem;
    white-space: nowrap;
  }

  &__item {
    padding: 0.125rem 0.25rem;
    padding-left: 0;
    padding-right: 0.5em;
    white-space: nowrap;
    width: max-content;
  }

  &__closebutton {
    margin-top: -3px;
    margin-right: -7px;
    padding: 0.75rem;
    padding-top: 0.625rem;
  }
}
</style>
